import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import type { ActiveCallObservedSession, ActiveCallPhase } from '@sbclite/core/sip/active-calls'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveCalls } from '../../hooks/useActiveCalls'
import { activeSessionCountLabel, formatActiveCallAge, formatDurationAtObservation, hasCachedSnapshot, isRuntimeUnavailable, phaseCountLabel } from '../../lib/active-calls-presentation'

function phaseLabel(phase: ActiveCallPhase): string {
  return phase.charAt(0).toUpperCase() + phase.slice(1)
}

function uri(value: string | undefined): string {
  return value || 'Not provided'
}

function traceUrl(callIds: readonly string[]): string {
  const ids = [...new Set(callIds.map((callId) => callId.trim()).filter(Boolean))]
  const params = new URLSearchParams()
  if (ids.length === 1) params.set('callId', ids[0]!)
  else if (ids.length > 1) params.set('relatedCallIds', ids.join(','))
  return `/system/sip-traffic-logs?${params.toString()}`
}

function SessionRow({ session }: { session: ActiveCallObservedSession }) {
  const [expanded, setExpanded] = useState(false)
  const allCallIds = session.legs.map((leg) => leg.callId)
  return (
    <>
      <tr className="border-t border-gray-200 align-top">
        <td className="px-4 py-3">
          <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} className="rounded p-1 hover:bg-gray-100">
            {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
            <span className="sr-only">{expanded ? 'Hide' : 'Show'} session legs</span>
          </button>
        </td>
        <td className="px-4 py-3 font-medium">{phaseLabel(session.phase)}</td>
        <td className="max-w-xs break-all px-4 py-3">{uri(session.fromUri)}</td>
        <td className="max-w-xs break-all px-4 py-3">{uri(session.toUri)}</td>
        <td className="px-4 py-3 whitespace-nowrap">{session.phase === 'connected' ? 'Connected duration' : 'Elapsed'} at observation: {formatDurationAtObservation(session)}</td>
        <td className="px-4 py-3 whitespace-nowrap">{session.engineId}<br /><span className="text-xs text-gray-500">Observed {formatActiveCallAge(session.observedAt)} ago</span></td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-12 py-3">
            <h3 className="mb-2 text-sm font-semibold text-gray-700">Runtime legs and trace links</h3>
            {session.legsTruncated && <p className="mb-2 text-sm font-medium text-amber-800">Leg list truncated at the engine cap; the trace links include only the listed Call-IDs.</p>}
            {allCallIds.length > 1 && <p className="mb-2"><Link className="font-medium text-blue-700 hover:underline" to={traceUrl(allCallIds)}>Open combined trace for all listed legs</Link></p>}
            {session.legs.length === 0 ? <p className="text-sm text-gray-600">No legs provided by the engine.</p> : (
              <ul className="space-y-1 text-sm">
                {session.legs.map((leg) => (
                  <li key={`${leg.side}:${leg.callId}`}>
                    <span className="mr-2 capitalize">{leg.side}</span>
                     <Link className="font-mono text-blue-700 hover:underline" to={traceUrl([leg.callId])}>{leg.callId}</Link>
                    {leg.protocol && <span className="ml-2 text-gray-600">{leg.protocol}</span>}
                    {leg.interfaceId && <span className="ml-2 text-gray-600">interface {leg.interfaceId}</span>}
                    {leg.peerId && <span className="ml-2 text-gray-600">peer {leg.peerId}</span>}
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export function OngoingCallsPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const query = useActiveCalls(isAdmin)
  const response = query.data?.data
  const sessions = response?.sessions ?? []
  const engines = response?.engines ?? []

  if (!isAdmin) {
    return <div className="rounded-lg border border-gray-200 bg-white p-8"><h1 className="text-2xl font-bold text-gray-900">Active Calls</h1><p className="mt-2 text-gray-600">Active call visibility is available to administrators only.</p></div>
  }

  const unavailable = isRuntimeUnavailable(response)
  const errorWithCache = query.isError && hasCachedSnapshot(response)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3"><h1 className="text-2xl font-bold text-gray-900">Active Calls</h1><span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">{activeSessionCountLabel(response)} active sessions</span></div>
          <p className="mt-1 text-gray-600">Read-only runtime sessions reported by the SIP engines. This view does not edit call state.</p>
        </div>
        <button type="button" onClick={() => query.refetch()} disabled={query.isFetching} className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</button>
      </div>

      {query.isLoading && <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">Loading active calls from the runtime engines…</div>}
      {query.isError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900"><strong>Active call fetch failed.</strong> {errorWithCache ? 'Showing the last successful snapshot, marked stale below.' : 'No runtime snapshot is available.'}</div>}
      {unavailable && <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"><strong>Runtime unavailable.</strong> No active-call count can be inferred while engines are unavailable.</div>}
      {response?.freshness === 'partial' && <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">Partial engine snapshot: counts cover available engines only.</div>}
      {errorWithCache && <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"><AlertTriangle className="mr-2 inline h-4 w-4" aria-hidden="true" />Stale data: last successful fetch {query.data?.data.generatedAt ? `${formatActiveCallAge(query.data.data.generatedAt)} ago` : 'at an unknown time'}.</div>}

      {response && <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700"><p><strong>{response.summary.listedSessions}</strong> sessions listed{response.summary.truncated ? ' (engine list truncated)' : ''}. Freshness: <strong>{response.freshness}</strong>. Last successful fetch: {new Date(response.generatedAt).toLocaleString()}. Engine observation timestamps are shown per row and are separate from fetch time. {engines.filter((engine) => engine.state === 'available').length}/{engines.length} engines available.</p><p className="mt-1">Listed phases: setup {response.summary.byPhase.setup}, ringing {response.summary.byPhase.ringing}, connected {response.summary.byPhase.connected} ({phaseCountLabel(response)}).</p></div>}

      {response && sessions.length === 0 && !unavailable && !query.isLoading && <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">No active sessions were listed in the available engine snapshot.</div>}
      {sessions.length > 0 && <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white"><table className="min-w-full text-left text-sm"><caption className="sr-only">Active runtime sessions</caption><thead className="bg-gray-50 text-xs uppercase text-gray-600"><tr><th className="w-12 px-4 py-3"><span className="sr-only">Expand</span></th><th className="px-4 py-3">Phase</th><th className="px-4 py-3">Caller</th><th className="px-4 py-3">Destination</th><th className="px-4 py-3">Duration</th><th className="px-4 py-3">Engine / observation</th></tr></thead><tbody>{sessions.map((session) => <SessionRow key={`${session.engineId}/${session.runtimeInstanceId}/${session.sessionId}`} session={session} />)}</tbody></table></div>}
    </div>
  )
}
